import React, { useState, useEffect } from 'react';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { initialFilters } from '../constants';
import { get, set, sequence, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import ServiceLogsFiltersComponent from './ServiceLogsFiltersComponent';
import ServiceLogsComponent from './ServiceLogsComponent';
import PropTypes from 'prop-types';
import { INSURANCE_SERVICE_REQUESTS_QUERY } from '../queries';
import { RETRIEVE_INSURANCE_SERVICE_LOGS } from '../mutations';
import { INSURANCE_SERVICE_LOG_RETRIEVED, INSURANCE_SERVICE_LOGS_RETRIEVED } from '../subscriptions';
import saveAs from 'file-saver';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';

const ServiceLogsFiltersContainer = ({ documentId }) => {
  const [filtersLens] = useChangeTrackingLens(initialFilters);
  const [insuranceServiceRequestsData, setInsuranceServiceRequestsData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [disabled, setDisabled] = useState({ filters: false, action: true });
  const [loadingStarted, setLoadingStarted] = useState(false);
  const [count, setCount] = useState(0);
  const showError = useSimpleError();
  const { t } = useTranslation();

  useQueryWithSimpleErrorHandling(INSURANCE_SERVICE_REQUESTS_QUERY, {
    variables: { documentId },
    fetchPolicy: 'network-only',
    skip: !documentId,
    onCompleted: data => {
      setInsuranceServiceRequestsData(data?.insuranceServiceRequests?.map(item => ({ ...item, selected: false })));
    }
  });

  const handleChangeRequest = requestType => {
    setLoadingStarted(false);
    (filtersLens.requestType |> set)(requestType);
    (filtersLens.insurersData |> set)(insuranceServiceRequestsData.filter(x => x.requestTypeCode === requestType));
  };

  const [retrieveInsuranceServiceLogs] = useMutation(RETRIEVE_INSURANCE_SERVICE_LOGS, {
    onError: error => {
      showError(error);
    }
  });

  useEffect(() => {
    const selectedCount = (filtersLens.insurersData |> sequence)?.filter(item => (item.selected |> get) === true)?.length;
    if (selectedCount) setDisabled(prev => ({ ...prev, action: false }));
    else setDisabled(prev => ({ ...prev, action: true }));
  }, [filtersLens]);

  const handleRetrieveInsuranceServiceLogs = () => {
    setRequests([]);
    const requestsUID = (filtersLens.insurersData |> sequence)
      ?.filter(item => (item.selected |> get) === true)
      ?.map(item => item.requestUID |> get);
    setCount(requestsUID.length);
    setLoadingStarted(true);
    retrieveInsuranceServiceLogs({
      variables: {
        input: {
          documentId,
          requestsUID
        }
      }
    });
    setDisabled({ filters: true, action: true });
  };

  useConditionalSubscription(INSURANCE_SERVICE_LOG_RETRIEVED, {
    onData: ({ data: { data } }) => {
      const selectedInsurerData = (filtersLens.insurersData |> sequence).find(
        item => (item.requestUID |> get) === data.insuranceServiceLogRetrieved.insuranceServiceLogResult.requestUID
      );
      setRequests(prevState => [
        ...prevState,
        {
          requestUID: data.insuranceServiceLogRetrieved.insuranceServiceLogResult.requestUID,
          details: data.insuranceServiceLogRetrieved.insuranceServiceLogResult.details,
          insurerName: selectedInsurerData?.insurerName |> get,
          period: selectedInsurerData?.period |> get
        }
      ]);
      setCount(prev => prev - 1);
    }
  });

  useConditionalSubscription(INSURANCE_SERVICE_LOGS_RETRIEVED, {
    onData: _ => {
      setDisabled({ filters: false, action: false });
    }
  });

  const handleDownload = (content, requestUID, logType, logDate) => {
    const selectedInsurerData = (filtersLens.insurersData |> sequence).find(item => (item.requestUID |> get) === requestUID);
    const insurerName = selectedInsurerData?.insurerName |> get;
    const period = selectedInsurerData?.period |> get;
    const requestType = filtersLens.requestType |> get;
    const fileName = `${capitalize(insurerName)}_${period}Months_${capitalize(requestType)}${capitalize(logType)}_${t('DATE', {
      date: logDate
    })}.xml`;

    const blob = new Blob([content], { type: 'text/xml' });
    saveAs(blob, fileName);
  };

  return (
    <>
      <ServiceLogsFiltersComponent
        filtersLens={filtersLens}
        onRequestTypeChange={handleChangeRequest}
        onRetrieveInsuranceServiceLogs={handleRetrieveInsuranceServiceLogs}
        disabled={disabled}
      />
      <ServiceLogsComponent requests={requests} loadingStarted={loadingStarted} count={count} onDownloadFile={handleDownload} />
    </>
  );
};

ServiceLogsFiltersContainer.propTypes = {
  documentId: PropTypes.number.isRequired
};

export default ServiceLogsFiltersContainer;
